import { useContext, useState } from 'react';
import _empty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { HomePageLayout, IVersionSupportedLiveGames, ILiveGamesBuckets } from 'commons/models/homePage.model';
import { AppContext, EMPTY_STRING, UserContext } from 'commons-ui';
import { GAME_KEY_MAP } from 'pages/admin/zooKeeper/utils/helper';
import {
	createVersionSupportedConfigFromLocal,
	deleteLayoutsInLocal,
	getHomePageLayoutFormattedData,
	getLayoutsFromLocal,
	getResponseFormat,
	HOME_LAYOUT_ZK_VALUES,
	removeLayoutsFromLocal,
	setLayoutsInLocal,
	validateAllBuckets
} from './helper';
import zkUseService from '../../../admin/zooKeeper/utils/useService';
import { IVersionBasedHomePageLayout } from './model';

const useService = () => {
	const { getKeyValue, updateKeyValue } = zkUseService();
	const { user } = useContext(UserContext);
	const { app, showAlert } = useContext(AppContext);
	const [globalForceUpdate, setGlobalForceUpdate] = useState<boolean>(false);
	let timer: any;
	const [confirmationDialog, setConfirmationDialog] = useState<{
		show: boolean;
		message?: string;
		header?: string;
	}>({
		show: false
	});

	const requestHomePageLayout = async ({
		keyName,
		serviceName
	}: {
		keyName: string;
		serviceName: string;
	}): Promise<IVersionBasedHomePageLayout[]> => {
		const { appName } = app;
		const result: {
			value: IVersionSupportedLiveGames;
			success: boolean;
		} = await getKeyValue({ game: GAME_KEY_MAP[appName], keyName, serviceName });

		const layouts: IVersionBasedHomePageLayout[] = [];
		if (result.success) {
			Object.keys(result.value).some((key) => {
				const versionRange: Array<string> = key.split('-');
				const bucketLayout: ILiveGamesBuckets = result.value[key];

				Object.keys(bucketLayout).forEach((bucket) => {
					const formattedData: IVersionBasedHomePageLayout = getHomePageLayoutFormattedData(
						parseInt(versionRange[0], 10),
						parseInt(versionRange[1], 10),
						bucket as keyof ILiveGamesBuckets,
						bucketLayout[bucket as keyof ILiveGamesBuckets] as HomePageLayout[]
					);
					layouts.push(formattedData);
				});

				return false;
			});
		}
		return layouts;
	};

	const turnGlobalForceUpdateOff = () => {
		setGlobalForceUpdate(false);
	};

	const clearLocalStorageForConfig = () => {
		removeLayoutsFromLocal();
		setGlobalForceUpdate(true);
		showAlert('Cleared All changes');
	};

	const setExpireTimeForLocalConfigStorage = () => {
		clearTimeout(timer);

		timer = setTimeout(() => {
			clearLocalStorageForConfig();
		}, 30 * 60 * 1000);
	};

	const getVersionSupportedHomepageLayout = async () => {
		const versionSupportedLayouts: IVersionBasedHomePageLayout[] = getLayoutsFromLocal();

		if (!_empty(versionSupportedLayouts)) {
			return getResponseFormat(versionSupportedLayouts, versionSupportedLayouts.length);
		}

		const layouts: IVersionBasedHomePageLayout[] = await requestHomePageLayout({
			keyName: HOME_LAYOUT_ZK_VALUES.keyName,
			serviceName: HOME_LAYOUT_ZK_VALUES.serviceName
		});

		setExpireTimeForLocalConfigStorage();

		setLayoutsInLocal(layouts);

		setGlobalForceUpdate(true);

		return getResponseFormat(layouts, layouts.length);
	};

	const removeVersionLayouts = (rowData: IVersionBasedHomePageLayout, showAlertParam: any) => {
		try {
			deleteLayoutsInLocal(rowData);
			setGlobalForceUpdate(true);
			showAlertParam(
				`versionRange: ${`${rowData.minAppVersion}-${rowData.maxAppVersion}`} and bucket: ${rowData.bucket} removed.`
			);
		} catch (error: any) {
			showAlertParam(error.response.data.error || 'Error occured while updated', { type: 'error' });
		}
	};

	const submitVersionLayouts = async () => {
		try {
			const versionSupportedLayouts: IVersionSupportedLiveGames = createVersionSupportedConfigFromLocal();
			validateAllBuckets(versionSupportedLayouts);
			const { appName } = app;
			const postData = {
				keyName: HOME_LAYOUT_ZK_VALUES.keyName,
				serviceName: HOME_LAYOUT_ZK_VALUES.serviceName,
				agent: _get(user, 'userName', ''),
				desc: 'Updated versionSupported game config',
				data: versionSupportedLayouts as any,
				game: GAME_KEY_MAP[appName]
			};
			await updateKeyValue(postData);
			removeLayoutsFromLocal();
			showAlert('Successfuly updated config');
		} catch (error: any) {
			showAlert(error.message || 'Error occured while updated', { type: 'error' });
		}
	};

	const handleSubmitAlertCancel = () => {
		setConfirmationDialog({ show: false });
	};

	const handleSubmitAlertConfirmation = () => {
		submitVersionLayouts();
		setConfirmationDialog({ show: false });
	};

	const setSubmitConfirmationDialog = () => {
		setConfirmationDialog({
			show: true,
			header: 'Submit All Changes?',
			message: EMPTY_STRING
		});
	};

	return {
		getVersionSupportedHomepageLayout,
		turnGlobalForceUpdateOff,
		globalForceUpdate,
		removeVersionLayouts,
		submitVersionLayouts,
		confirmationDialog,
		handleSubmitAlertCancel,
		handleSubmitAlertConfirmation,
		setSubmitConfirmationDialog,
		clearLocalStorageForConfig
	};
};

export default useService;
